import { cloneWorkflowGraph } from './cloneWorkflowGraph.js';
import { expandWorkflowFolderStepIds } from './expandWorkflowFolderStepIds.js';
import { getWorkflowGraphSelection } from './getWorkflowGraphSelection.js';
import { randomId } from '../utils/randomId.js';
import { buildGraphFromSteps } from './duplicateWorkflowStepsByIds/buildGraphFromSteps.js';
import { findDisconnectedSubgraphs } from './duplicateWorkflowStepsByIds/findDisconnectedSubgraphs.js';
import { getLastStepInSubgraph } from './duplicateWorkflowStepsByIds/getLastStepInSubgraph.js';
import { buildRemappedGraph } from './duplicateWorkflowStepsByIds/buildRemappedGraph.js';
import { connectSubgraphsInSequence } from './duplicateWorkflowStepsByIds/connectSubgraphsInSequence.js';
import { determineTargetRelation } from './duplicateWorkflowStepsByIds/determineTargetRelation.js';

export const duplicateWorkflowStepsByIds = ({ workflow, ids, userId, parentStepId, copiedSteps, copiedGraph }) => {
  const sourceSteps = copiedSteps || [...(workflow?.steps || [])];
  const sourceGraph = copiedGraph || (copiedSteps ? buildGraphFromSteps(copiedSteps) : cloneWorkflowGraph(workflow?.graph));
  const targetSteps = [...(workflow?.steps || [])];
  const targetGraph = cloneWorkflowGraph(workflow?.graph);
  const sourceStepById = new Map(sourceSteps.map((step) => [step.id, step]));
  const filteredIds = expandWorkflowFolderStepIds({ graph: sourceGraph, steps: sourceSteps, stepIds: ids });

  // Grab only the action ids
  const actionIds = filteredIds.filter((n) => {
    const step = sourceSteps.find((m) => m.id === n);
    return !['schedule'].includes(step?.appSlug) && step?.type !== 'trigger';
  });

  // Build a clone id map
  const idMap = {};
  filteredIds?.forEach((n) => {
    idMap[n] = randomId(12, 'all');
  });

  const newDate = new Date().toISOString();

  // Duplicate the steps
  const newSteps = [];
  for (let i = 0; i < filteredIds?.length; ++i) {
    const stepId = filteredIds[i];
    const step = { ...sourceStepById.get(stepId) };
    const newStepId = idMap[stepId];
    const newStep = {
      ...step,
      id: newStepId,
      // Tenant ownership of a duplicated step belongs to the TARGET workflow's company,
      // never the source row's. Without this, cross-workflow paste from a different
      // tenant inserts an orphan row invisible to selectWorkflow's company-scoped read.
      companyId: workflow?.companyId,
      workflowId: workflow?.id,
      createdOn: newDate,
      updatedOn: newDate,
      createdBy: userId,
      updatedBy: userId
    };
    newSteps.push(newStep);
  }

  const selectedSourceGraph = getWorkflowGraphSelection({ graph: sourceGraph, stepIds: actionIds });
  let newGraph = buildRemappedGraph({ actionIds, sourceGraph: selectedSourceGraph, idMap });
  const subgraphs = findDisconnectedSubgraphs(actionIds, selectedSourceGraph);

  if (subgraphs.length > 1) {
    newGraph = connectSubgraphsInSequence({ graph: newGraph, subgraphs, sourceGraph: selectedSourceGraph, idMap });
  }

  // Determine the connection strategy based on context
  if (copiedSteps || parentStepId) {
    // Handle parent connection if specified
    if (parentStepId) {
      const parentStep = targetGraph[parentStepId];
      if (!parentStep) {
        return null;
      }

      if (subgraphs.length === 0) {
        return null;
      }

      // Connect subgraphs in sequence: parent → subgraph1 → subgraph2 → ... → original_parent_exit
      const firstSubgraph = subgraphs[0];
      const firstNewStepId = idMap[firstSubgraph.root];

      // Get the last step of the final subgraph for parent connection
      const lastSubgraph = subgraphs[subgraphs.length - 1];
      const lastNewStepId = idMap[getLastStepInSubgraph(lastSubgraph, selectedSourceGraph)];

      // Determine the appropriate relationship type and index
      const { relation, index } = determineTargetRelation(parentStepId, parentStep, targetSteps, copiedSteps, selectedSourceGraph);

      // Handle different relationship types
      if (relation === 'exit') {
        const originalParentExit = parentStep.exit;

        // Connect parent to first new step via exit
        targetGraph[parentStepId] = {
          ...parentStep,
          exit: firstNewStepId
        };

        // Connect last new step to original parent's exit (preserving the original flow)
        // This inserts the copied steps between the parent and its original next step
        if (newGraph[lastNewStepId]) {
          newGraph[lastNewStepId] = {
            ...newGraph[lastNewStepId],
            exit: originalParentExit
          };
        }
      } else if (relation === 'child') {
        const originalParentChild = parentStep.child;

        // Connect parent to first new step via child
        targetGraph[parentStepId] = {
          ...parentStep,
          child: firstNewStepId
        };

        // Connect last new step to original parent's child (preserving the original flow)
        // This inserts the copied steps between the parent and its original child
        if (newGraph[lastNewStepId]) {
          newGraph[lastNewStepId] = {
            ...newGraph[lastNewStepId],
            exit: originalParentChild
          };
        }
      } else if (relation === 'branch' && index !== null) {
        const originalParentBranches = [...(parentStep.branches || [])];
        const originalBranchConnection = originalParentBranches[index];

        // Ensure branches array is large enough
        while (originalParentBranches.length <= index) {
          originalParentBranches.push(null);
        }

        // Connect parent to first new step via branch
        originalParentBranches[index] = firstNewStepId;
        targetGraph[parentStepId] = {
          ...parentStep,
          branches: originalParentBranches
        };

        // For branches, only connect to original branch connection if there was one
        // This allows replacing existing branch content or adding to empty branch slots
        if (newGraph[lastNewStepId] && originalBranchConnection) {
          newGraph[lastNewStepId] = {
            ...newGraph[lastNewStepId],
            exit: originalBranchConnection
          };
        }
      }
    }
  } else {
    if (subgraphs.length > 0) {
      const firstNewStepId = idMap[subgraphs[0].root];
      const lastSubgraph = subgraphs[subgraphs.length - 1];
      const lastOriginalStepId = getLastStepInSubgraph(lastSubgraph, selectedSourceGraph);
      const lastNewStepId = idMap[lastOriginalStepId];
      const originalLastExit = targetGraph[lastOriginalStepId]?.exit;

      if (targetGraph[lastOriginalStepId]) {
        targetGraph[lastOriginalStepId] = {
          ...targetGraph[lastOriginalStepId],
          exit: firstNewStepId
        };
      }

      if (newGraph[lastNewStepId]) {
        newGraph[lastNewStepId] = {
          ...newGraph[lastNewStepId],
          exit: originalLastExit || null
        };
      }
    }
  }

  // Merge graphs and steps
  const fullGraph = { ...targetGraph, ...newGraph };
  const fullSteps = [...targetSteps, ...newSteps];

  return { graph: fullGraph, steps: fullSteps, newSteps, timestamp: newDate };
};
